import { useCallback, useEffect, useRef, useState } from 'react';

import { AutocompletePrediction } from 'components/ucl/autocomplete-options-dropdown/types';
import type { PlacesSessionToken } from 'hooks/geolocation/types';
import useGoogleGeolocationLibrary from 'hooks/geolocation/use-google-geolocation-library';
import { useLocale } from 'state/intl';

const LOCATION_LENGTH_THRESHOLD_FOR_PREDICTIONS = 3;
const ADDRESS_SEARCH_DEBOUNCE_MS = 350;

export interface UseLocationPredictionsSessionHelpers {
  getSessionToken: () => PlacesSessionToken | undefined;
  resetSessionToken: () => void;
}

function useLocationPredictions(
  location: string,
  { addressesOnly = false, enabled = true }
): [AutocompletePrediction[], UseLocationPredictionsSessionHelpers] {
  const { libraryLoaded } = useGoogleGeolocationLibrary();
  const { region } = useLocale();
  const [predictions, setPredictions] = useState<AutocompletePrediction[]>([]);
  const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | undefined>(
    undefined
  );
  const sessionTokenRef = useRef<PlacesSessionToken | undefined>(undefined);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Incremented when cancelling a search; used to discard stale callbacks from in-flight requests. */
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (libraryLoaded && !autocompleteServiceRef.current) {
      autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
    }
  }, [libraryLoaded]);

  const resetSessionToken = useCallback(() => {
    sessionTokenRef.current = undefined;
  }, []);

  const getSessionToken = useCallback((): PlacesSessionToken | undefined => {
    if (!libraryLoaded) {
      return undefined;
    }
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    }
    return sessionTokenRef.current;
  }, [libraryLoaded]);

  const trimmedLocation = location.trim();

  useEffect(() => {
    if (!enabled || !libraryLoaded) {
      requestIdRef.current += 1;
      setPredictions([]);
      resetSessionToken();
      return;
    }

    if (!trimmedLocation || trimmedLocation.length < LOCATION_LENGTH_THRESHOLD_FOR_PREDICTIONS) {
      requestIdRef.current += 1;
      setPredictions([]);
      if (!trimmedLocation) {
        resetSessionToken();
      }
      return;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;

      const currentRequestId = requestIdRef.current;

      const sessionToken = getSessionToken();
      const request: google.maps.places.AutocompletionRequest = {
        input: trimmedLocation,
        componentRestrictions: { country: region },
        ...(addressesOnly && { types: ['address'] }),
        ...(sessionToken && { sessionToken }),
      };

      autocompleteServiceRef.current?.getPlacePredictions(
        request,
        (results, status: google.maps.places.PlacesServiceStatus) => {
          if (currentRequestId !== requestIdRef.current) {
            return;
          }
          const mappedResults =
            status === window.google.maps.places.PlacesServiceStatus.OK && results?.length
              ? results.map(prediction => ({
                  placeId: prediction.place_id,
                  description: prediction.description,
                  mainText: prediction.structured_formatting.main_text,
                  secondaryText: prediction.structured_formatting.secondary_text,
                }))
              : [];
          setPredictions(mappedResults);
        }
      );
    }, ADDRESS_SEARCH_DEBOUNCE_MS);

    return () => {
      requestIdRef.current += 1;
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [
    addressesOnly,
    enabled,
    getSessionToken,
    libraryLoaded,
    trimmedLocation,
    region,
    resetSessionToken,
  ]);

  return [predictions, { getSessionToken, resetSessionToken }];
}

export default useLocationPredictions;
